import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
// material
import { Box, TextField, IconButton, Typography, Popover } from '@mui/material';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
// hooks
import useAuth from '../../hooks/useAuth';
// redux
import { useDispatch } from '../../redux/store';
import { addMessage } from '../../redux/slices/internalChat';
// utils
import axios from '../../utils/axios';
// components
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

InternalChatInput.propTypes = {
    chatId: PropTypes.string.isRequired,
    chatMode: PropTypes.string,
    participants: PropTypes.array,
    replyingTo: PropTypes.object,
    onCancelReply: PropTypes.func
};

export default function InternalChatInput({ chatId, chatMode, participants, replyingTo, onCancelReply }) {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const isMountedRef = useRef(null);
    const fileInputRef = useRef(null);

    const [message, setMessage] = useState('');
    const [file, setFile] = useState(null);

    // Emoji Picker State
    const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
    const openEmoji = Boolean(emojiAnchorEl);

    // Audio Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerIntervalRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Cleanup on unmount
    useEffect(() => () => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
    }, []);

    const handleOpenEmoji = (event) => {
        setEmojiAnchorEl(event.currentTarget);
    };

    const handleCloseEmoji = () => {
        setEmojiAnchorEl(null);
    };

    const handleEmojiSelect = (emoji) => {
        setMessage((prev) => prev + emoji.native);
    };

    const handleAttach = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const { files } = event.target;
        if (files && files.length > 0) {
            const selectedFile = files[0];
            selectedFile.preview = URL.createObjectURL(selectedFile);
            setFile(selectedFile);
        }
    };

    const handlePaste = (e) => {
        if (e.clipboardData && e.clipboardData.files.length > 0) {
            const pastedFile = e.clipboardData.files[0];
            if (pastedFile.type.startsWith('image/') || pastedFile.type.startsWith('video/')) {
                e.preventDefault();
                pastedFile.preview = URL.createObjectURL(pastedFile);
                setFile(pastedFile);
            }
        }
    };

    const sendAudioMessage = useCallback(async (audioFile) => {
        try {
            const formData = new FormData();
            formData.append('attachment', audioFile);

            // Backend expects attachment field
            const response = await axios.post(`/api/internal-chat/${chatId}/messages`, formData);

            if (response.data && isMountedRef.current) {
                dispatch(addMessage({ conversationId: chatId, message: response.data }));
            }
        } catch (error) {
            console.error('Failed to send audio message:', error);
        }
    }, [chatId, dispatch]);

    const stopStreamTracks = () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerIntervalRef.current = setInterval(() => {
                if (isMountedRef.current) {
                    setRecordingTime(prev => prev + 1);
                }
            }, 1000);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Não foi possível acessar o microfone. Verifique as permissões.');
        }
    };

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' }); // Chrome/Firefox default
                const audioFile = new File([audioBlob], 'audio-message.webm', { type: 'audio/webm' });

                sendAudioMessage(audioFile);

                // Cleanup
                stopStreamTracks();
                if (isMountedRef.current) {
                    setIsRecording(false);
                }
                clearInterval(timerIntervalRef.current);
                audioChunksRef.current = [];
            };
            mediaRecorderRef.current.stop();
        }
    }, [isRecording, sendAudioMessage]);

    const cancelRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            stopStreamTracks();
            if (isMountedRef.current) {
                setIsRecording(false);
            }
            clearInterval(timerIntervalRef.current);
            audioChunksRef.current = [];
        }
    }, [isRecording]);

    const handleSend = useCallback(async () => {
        if (!message.trim() && !file) return;

        const currentMessage = message;
        const currentFile = file;

        // Limpar estado imediatamente para UX
        setMessage('');
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        try {
            let response;
            if (currentFile) {
                const formData = new FormData();
                if (currentMessage) formData.append('content', currentMessage);
                formData.append('attachment', currentFile);
                if (replyingTo) {
                    formData.append('replyTo', JSON.stringify({
                        id: replyingTo._id,
                        senderName: replyingTo.senderId.name,
                        content: replyingTo.content || 'Anexo'
                    }));
                }

                response = await axios.post(`/api/internal-chat/${chatId}/messages`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                const payload = {
                    content: currentMessage,
                };
                if (replyingTo) {
                    payload.replyTo = {
                        id: replyingTo._id,
                        senderName: replyingTo.senderId.name,
                        content: replyingTo.content || 'Mensagem'
                    };
                }
                response = await axios.post(`/api/internal-chat/${chatId}/messages`, payload);
            }

            if (response.data && isMountedRef.current) {
                dispatch(addMessage({ conversationId: chatId, message: response.data }));
            }
            if (isMountedRef.current && onCancelReply) {
                onCancelReply(); // Clear reply after sending
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            // Opcional: restaurar mensagem em caso de erro
        }
    }, [message, file, replyingTo, chatId, dispatch, onCancelReply]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);


    return (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(145, 158, 171, 0.24)' }}>
            {/* Reply Banner */}
            {replyingTo && (
                <Box sx={{
                    p: 1,
                    pl: 2,
                    mb: 1,
                    bgcolor: 'background.neutral',
                    borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            Respondendo a {replyingTo.senderId.name}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ color: 'text.secondary', maxWidth: 300 }}>
                            {replyingTo.content || 'Anexo'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onCancelReply}>
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                </Box>
            )}

            {file && (
                <Box sx={{ mb: 1, p: 1, border: '1px dashed grey', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {file.type.startsWith('image/') && (
                            <Box component="img" src={file.preview} sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', mr: 1 }} />
                        )}
                        {file.type.startsWith('video/') && (
                            <Box component="video" src={file.preview} sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', mr: 1 }} />
                        )}
                        <Typography variant="caption">{file.name}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                    }}>
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>

                {/* Announcement Mode Check */}
                {chatMode === 'announcement' && !participants?.find(p => p.userId._id === user._id)?.isAdmin ? (
                    <Box sx={{ width: '100%', textAlign: 'center', py: 1, bgcolor: 'background.neutral', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Apenas administradores podem enviar mensagens neste grupo.
                        </Typography>
                    </Box>
                ) : (
                    /* Normal Input Area */
                    <>
                        {/* Audio Recording UI */}
                        {isRecording ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'error.main',
                                            borderRadius: '50%',
                                            mr: 1,
                                            animation: 'pulse 1.5s infinite ease-in-out',
                                            '@keyframes pulse': {
                                                '0%': { opacity: 1 },
                                                '50%': { opacity: 0.4 },
                                                '100%': { opacity: 1 },
                                            }
                                        }}
                                    />
                                    <Typography variant="body2" color="error">
                                        Gravando... {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:{(recordingTime % 60).toString().padStart(2, '0')}
                                    </Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1 }} />
                                <IconButton onClick={cancelRecording} color="error">
                                    <Iconify icon="eva:trash-2-fill" width={24} height={24} />
                                </IconButton>
                                <IconButton onClick={stopRecording} color="primary">
                                    <Iconify icon="eva:paper-plane-fill" width={24} height={24} />
                                </IconButton>
                            </Box>
                        ) : (
                            <>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />

                                <IconButton sx={{ mr: 1 }} onClick={handleOpenEmoji}>
                                    <Iconify icon="eva:smiling-face-fill" width={24} height={24} />
                                </IconButton>

                                <Popover
                                    open={openEmoji}
                                    anchorEl={emojiAnchorEl}
                                    onClose={handleCloseEmoji}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                >
                                    <Picker
                                        data={data}
                                        onEmojiSelect={handleEmojiSelect}
                                        theme="light"
                                        locale="pt"
                                    />
                                </Popover>

                                <IconButton sx={{ mr: 1 }} onClick={handleAttach}>
                                    <Iconify icon="eva:attach-2-fill" width={24} height={24} />
                                </IconButton>

                                <IconButton sx={{ mr: 1 }} onClick={startRecording} color="primary">
                                    <Iconify icon="eva:mic-fill" width={24} height={24} />
                                </IconButton>

                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Digite sua mensagem..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    onPaste={handlePaste}
                                    multiline
                                    maxRows={4}
                                    inputProps={{ maxLength: 1024 }}
                                />
                                <Box component="span" sx={{ ml: 1 }}>
                                    <IconButton color="primary" onClick={handleSend} disabled={!message.trim() && !file}>
                                        <Iconify icon="eva:paper-plane-fill" width={24} height={24} />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
